import { UniqueIdentifier } from "@dnd-kit/core";
import { ImsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import TruckIconFilled from "@redotech/redo-icons/truck-01_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { ShipmentViewsContext } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { ShipmentsSystemView } from "@redotech/redo-model/views/default-shipment-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";
import { useDefaultViewName } from "./get-default-view-name";

export const ShipmentsItem = memo(function ShipmentsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const {
    views,
    folders,
    openEditFolderModal,
    openCreateFolderModal,
    updateOrdering,
  } = useRequiredContext(ShipmentViewsContext);
  const urlPrefix = `${routes.inventoryManagementBaseUrl}/shipments`;

  const hasPermissionToViewItem = useHasPermission(
    ImsPermissions.ACCESS_SHIPMENTS,
  );

  const defaultViewName = useDefaultViewName(views, ShipmentsSystemView.ALL);

  const viewsWithoutFolders = useMemo(() => {
    return views?.filter((view) => !view.folderId) || [];
  }, [views]);

  const foldersWithViews = useMemo<FolderWithView<"shipments">[]>(() => {
    return (
      folders?.map((folder) => {
        return {
          folder,
          views: views?.filter((view) => view.folderId === folder._id) || [],
        };
      }) || []
    );
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"shipments">(
      flattenedItems,
      views || [],
      folders || [],
    );
    updateOrdering(result.views, result.folders);
  });

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  if (!hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${encodeURIComponent(defaultViewName)}`,
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={TruckIconFilled}
      friendlyName="Shipments"
      id="shipments"
      UnfilledIcon={TruckIcon}
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="shipments-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});
